import { useAuth } from '@/hooks';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import ct from 'countries-and-timezones';
import { getTimeZone } from '@/lib/date';

// mixpanel-browser is 177.7 KB gzipped and was statically imported here, so it
// loaded on first paint for every session even though nothing about rendering
// needs it. Every use below is imperative (an effect or an event handler), so
// the module is fetched on first use instead.
//
// One shared promise, so `init` and `track` get the same instance and their
// callbacks run in the order the calls were made. Load failures are swallowed:
// analytics must never take the app down with it.
let mixpanelModule = null;
const loadMixpanel = () => {
  mixpanelModule ??= import('mixpanel-browser').then((m) => m.default);
  return mixpanelModule;
};
const withMixpanel = (fn) => {
  loadMixpanel()
    .then(fn)
    .catch(() => {});
};

const getCountryCode = () => {
  try {
    return ct.getCountryForTimezone(getTimeZone());
  } catch {
    return {};
  }
};

export const useMixpanel = () => {
  const { currUser } = useAuth();
  const { feSegment } = useFlags();
  const country = getCountryCode();
  const initMixpanel = () => {
    if (!import.meta.env.VITE_MIXPANEL) return;
    const accountName = window?.location?.hostname.split('.')[0];
    withMixpanel((mixpanel) => {
      mixpanel.init(import.meta.env.VITE_MIXPANEL, {
        loaded: () => {
          mixpanel.identify(currUser?.id);
          mixpanel.set_group('company', accountName);
          mixpanel.people.set({
            name: currUser?.first_name,
            role: currUser?.role,
            distinct_id: currUser?.id,
            account: accountName,
            email: currUser?.email,
            subscription: currUser?.account?.status,
          });
          mixpanel.register({
            name: currUser?.first_name,
            role: currUser?.role,
            distinct_id: currUser?.id,
            account: accountName,
            email: currUser?.email,
            subscription: currUser?.account?.status,
          });
        },
      });
    });
  };
  const initSegment = () => {
    if (!import.meta.env.VITE_SEGMENT) return;
    const accountName = window?.location?.hostname.split('.')[0];
    window.segment = AnalyticsBrowser.load({
      writeKey: import.meta.env.VITE_SEGMENT,
    });
    window.segment.identify(currUser?.id, {
      name: currUser?.first_name,
      role: currUser?.role,
      distinct_id: currUser?.id,
      account: accountName,
      email: currUser?.email,
      subscription: currUser?.account?.status,
      country: country?.id,
    });
  };

  const trackMixpanel = ({ event, prop = {} }) => {
    if (!event || !import.meta.env.VITE_MIXPANEL) return;
    withMixpanel((mixpanel) => mixpanel.track(event, prop));
  };

  const trackSegment = ({ event, prop = {} }) => {
    if (!event || !import.meta.env.VITE_SEGMENT || !window.segment) return;
    window.segment.track(event, prop);
  };

  return {
    init: feSegment ? initSegment : initMixpanel,
    track: feSegment ? trackSegment : trackMixpanel,
  };
};

export const useSignupMixpanel = () => {
  const { feSegment } = useFlags();
  const initMixpanel = () => {
    if (!import.meta.env.VITE_MIXPANEL) return;
    withMixpanel((mixpanel) => mixpanel.init(import.meta.env.VITE_MIXPANEL));
  };

  const initSegment = () => {
    if (!import.meta.env.VITE_SEGMENT) return;
    window.segment = AnalyticsBrowser.load({
      writeKey: import.meta.env.VITE_SEGMENT,
    });
    window.segment.identify('kula-account-creation');
  };

  const trackMixpanel = ({ event, prop = {} }) => {
    if (!event || !import.meta.env.VITE_MIXPANEL) return;
    setTimeout(() => {
      withMixpanel((mixpanel) => mixpanel.track(event, prop));
    }, 300);
  };

  const trackSegment = ({ event, prop = {} }) => {
    if (!event || !import.meta.env.VITE_SEGMENT) return;
    setTimeout(() => {
      window.segment.track(event, prop);
    }, 300);
  };

  return {
    init: feSegment ? initSegment : initMixpanel,
    track: feSegment ? trackSegment : trackMixpanel,
  };
};
